import React, { useCallback } from 'react';
import styled from 'styled-components';
import { ReactComponent as CaretLeftIcon } from '../../../../assets/images/caret-left.svg';
import { Spinner } from '../../../../components/atoms/Spinner';
import { useMessageForm } from '../../forms';

const StyledContainer = styled.div<{ $disabled?: boolean }>`
  position: relative;
  overflow: hidden;
  border: 1px solid #d7d3d0;
  border-radius: 0.75rem;
  margin-top: 0.75rem;
  padding: 0.35rem 0.6rem;
  background-color: ${({ $disabled }) => ($disabled ? '#f0f0f0' : '#ffffff')};

  &:focus-within {
    outline: ${({ $disabled }) =>
      $disabled ? 'none' : '2px solid var(--rg-primary-color, #1249a1)'};
    outline-offset: -2px;
  }
`;

const StyledTextarea = styled.textarea<{ $disabled?: boolean }>`
  color: #232323;
  display: flex;
  border: none;
  width: 100%;
  padding: 0.5rem;
  padding-right: 3rem;
  max-height: 6rem;
  min-height: 4.6875rem;
  resize: none;
  font-size: 0.875rem;
  font-weight: 400;
  font-family: inherit;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &:hover,
  &:focus,
  &:active {
    outline: none;
    border: none;
  }

  &:disabled {
    background-color: #f0f0f0;
  }

  &::placeholder {
    color: #57534e;
  }
`;

const StyledSendButton = styled.button<{ $disabled?: boolean }>`
  position: absolute;
  right: 0.6rem;
  bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  background: ${({ $disabled }) =>
    $disabled ? 'transparent' : 'var(--rg-primary-color, #232323)'};
  border-radius: 999px;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  color: #1249a1;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition:
    background 0.2s ease,
    opacity 0.2s ease,
    transform 0.15s ease;

  &:not(:disabled):hover {
    background: var(--rg-primary-hover, #3a3a3a);
  }

  &:not(:disabled):active {
    transform: scale(0.92);
  }

  svg {
    width: 1rem;
    height: 1rem;
    transform: rotate(180deg);
    transition: fill 0.2s ease;
    path {
      fill: ${({ $disabled }) =>
        $disabled ? '#a9a29d' : 'var(--rg-text-on-primary, #faf7f2)'};
    }
  }
`;

type Props = {
  placeholder?: string;
};

export function MessageInput({
  placeholder = 'Write a custom message...',
}: Readonly<Props>) {
  const {
    values,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    submitForm,
  } = useMessageForm();

  const disabled = isSubmitting;
  const canSend = isValid && !isSubmitting;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (canSend) {
          submitForm();
        }
      }
    },
    [canSend, submitForm]
  );

  return (
    <StyledContainer $disabled={disabled}>
      <StyledTextarea
        name="message"
        value={values.message}
        placeholder={placeholder}
        aria-required
        aria-label="Message"
        $disabled={disabled}
        disabled={disabled}
        rows={1}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      <StyledSendButton
        type="button"
        $disabled={!canSend}
        disabled={!canSend}
        onClick={submitForm}
        aria-label="Send message"
      >
        {isSubmitting ? <Spinner /> : <CaretLeftIcon />}
      </StyledSendButton>
    </StyledContainer>
  );
}
